'use client'

import { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Inter, Lato } from 'next/font/google'
import { localAssetPath } from '@/lib/assets'
import { LandingLoginProvider } from '@/app/landing/LandingLoginProvider'
import { useComboPricing } from '@/app/landing/hooks/useComboPricing'
import { SOCIAL_LINKS } from '@/app/landing/components/NewUIFooter'
import { PaymentPendingModal } from '@/components/ui/PaymentPendingModal'
import { PaymentRequiredModal } from '@/components/ui/PaymentRequiredModal'
import { PaymentSuccessModal } from '@/components/ui/PaymentSuccessModal'
import { event as gaEvent } from '@/lib/ga'
import { analytics, campaignPricingViewedEvent } from '@/lib/analytics'
import { formatLandingPrice, showLandingGstNote } from '@/lib/formatLandingPrice'
import {
  fetchProductPacks,
  getInterviewPracticeCurrency,
  getInterviewPracticeDisplayPrice,
  isComboPack,
  type ProductPack,
} from '@/lib/productPacks'
import { getAuthToken } from '@/lib/auth'
import { CampaignBodyClass } from './CampaignBodyClass'
import { PricingDialog } from './PricingDialog'
import {
  CAMPAIGN_CLAIM_UNAVAILABLE,
  CAMPAIGN_DEFAULT_OFFER_PREVIEW,
  CAMPAIGN_ICON_CHECK,
  CAMPAIGN_ICON_CTA_ARROW,
  CAMPAIGN_LOGO_ON_DARK,
  CAMPAIGN_LOGO_ON_LIGHT,
} from './sharedAssets'
import { CampaignHero } from './CampaignHero'
import { getCampaignConfigForPath } from './campaignRegistry'
import { useScrollBorderRotation } from './useScrollBorderRotation'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import type { InterviewPracticeCampaignConfig } from './types'
import styles from './interview-practice-campaign.module.css'

const inter = Inter({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
  display: 'swap',
  variable: '--font-inter',
})

const lato = Lato({
  subsets: ['latin'],
  weight: ['400', '700', '900'],
  display: 'swap',
  variable: '--font-campaign-heading',
})

function findSkillAssessmentPack(packs: ProductPack[]): ProductPack | null {
  return (
    packs.find(
      (pack) => pack.payment_plan_type === 'skill_assessment' && pack.is_active !== false,
    ) ?? null
  )
}

function CampaignFooter({ footerId }: { footerId: string }) {
  return (
    <footer id={footerId} className={styles.campaignFooter}>
      <div className={styles.campaignFooterInner}>
        <div className={styles.campaignFooterBrand}>
          <img
            src={CAMPAIGN_LOGO_ON_DARK}
            alt="meritlens"
            width={280}
            height={90}
            className={styles.campaignFooterLogo}
            onError={handleAssetError}
          />
          <p className={styles.campaignFooterTagline}>
            Practice smarter. Interview better.
            <br />
            Land the role you deserve.
          </p>
        </div>
        <div className={styles.campaignFooterDivider} aria-hidden="true" />
        <div className={styles.campaignFooterSocialBlock}>
          <div className={styles.campaignFooterSocial}>
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.campaignSocialLink}
              aria-label="meritlens on Instagram"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
                />
              </svg>
            </a>
            <a
              href={SOCIAL_LINKS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.campaignSocialLink}
              aria-label="meritlens on LinkedIn"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                />
              </svg>
            </a>
            <a
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.campaignSocialLink}
              aria-label="meritlens on Facebook"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                />
              </svg>
            </a>
            <a
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.campaignSocialLink}
              aria-label="meritlens on YouTube"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                />
              </svg>
            </a>
          </div>
          <p className={styles.campaignFooterCopyright}>© 2026 meritlens. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

function OfferCard({
  id,
  cardTitle,
  bullets,
  offerPreviewImage,
  microcopy,
  displayPrice,
  currency,
  onStart,
  claiming,
  error,
  className,
}: {
  id?: string
  cardTitle: string
  bullets: readonly string[]
  offerPreviewImage: string
  microcopy: string
  displayPrice: number | undefined
  currency: string
  onStart: () => void
  claiming: boolean
  error: string | null
  className?: string
}) {
  return (
    <aside id={id} className={`${styles.offerCard} ${className ?? ''}`} aria-label="Start your interview">
      <div className={styles.offerCardImageWrap}>
        <img
          src={offerPreviewImage}
          alt=""
          className={styles.offerCardImage}
          width={330}
          height={200}
          decoding="async" onError={handleAssetError} />
      </div>
      <div className={styles.offerCardBody}>
        <div className={styles.offerCardContent}>
          <div className={styles.offerCardTop}>
            <h2 className={styles.offerCardTitle}>{cardTitle}</h2>
            <ul className={styles.offerCardList}>
              {bullets.map((line) => (
                <li key={line}>
                  <img src={CAMPAIGN_ICON_CHECK} alt="" width={24} height={24} aria-hidden="true" onError={handleAssetError} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.offerCardPricing}>
            <div className={styles.offerPriceRow}>
              <span className={styles.offerPrice}>
                {displayPrice != null
                  ? formatLandingPrice(displayPrice, currency)
                  : formatLandingPrice(199, 'INR')}
              </span>
              {showLandingGstNote(currency) ? (
                <span className={styles.offerGst}>+GST</span>
              ) : null}
            </div>
            <p className={styles.offerMicrocopy}>{microcopy}</p>
          </div>
        </div>
        {error ? <p className={styles.offerError}>{error}</p> : null}
        <button type="button" className={styles.offerCta} onClick={onStart} disabled={claiming}>
          Start My Interview
          <img src={CAMPAIGN_ICON_CTA_ARROW} alt="" width={16} height={16} aria-hidden="true" onError={handleAssetError} />
        </button>
      </div>
    </aside>
  )
}

/**
 * Image fallback: stage/prod request campaign art from the CDN (NEXT_PUBLIC_ASSET_BASE_URL). If a
 * role image was never uploaded there, swap once to the same-origin `/assets/...` copy instead of
 * leaving a broken image. `srcset` is dropped too, or the browser keeps picking the CDN candidate.
 */
function handleAssetError(event: SyntheticEvent<HTMLImageElement>): void {
  const image = event.currentTarget
  if (image.dataset.localFallback === 'true') return
  const fallback = localAssetPath(image.currentSrc || image.src)
  if (!fallback || fallback === image.src) return
  image.dataset.localFallback = 'true'
  image.removeAttribute('srcset')
  image.src = fallback
}

function InterviewPracticeCampaignInner({
  config: configFromPage,
}: {
  config: InterviewPracticeCampaignConfig
}) {
  const pathname = usePathname()
  const config = useMemo(() => {
    const fromPath = getCampaignConfigForPath(pathname)
    return fromPath ?? configFromPage
  }, [configFromPage, pathname])

  const [catalogPacks, setCatalogPacks] = useState<ProductPack[]>([])
  const [catalogReady, setCatalogReady] = useState(false)
  const [claimError, setClaimError] = useState<string | null>(null)
  const [claiming, setClaiming] = useState(false)
  const [pricingOpen, setPricingOpen] = useState(false)
  const [pricingPacks, setPricingPacks] = useState<ProductPack[]>([])
  const [pricingLoading, setPricingLoading] = useState(false)

  // The pricing overlay is portaled over the campaign page; lock the page behind it so a
  // scroll gesture inside the dialog never scrolls the campaign to a different section.
  useBodyScrollLock(pricingOpen)

  const ids = useMemo(
    () => ({
      offer: `${config.idPrefix}-offer`,
      offerDesktop: `${config.idPrefix}-offer-desktop`,
      why: `${config.idPrefix}-why`,
      whyHeading: `${config.idPrefix}-why-heading`,
      footer: `${config.idPrefix}-footer`,
    }),
    [config.idPrefix],
  )

  const featureListRef = useScrollBorderRotation<HTMLUListElement>()

  const offerPreviewImage = config.offer.offerPreviewImage ?? CAMPAIGN_DEFAULT_OFFER_PREVIEW
  const offerMicrocopy =
    config.offer.microcopy ?? 'One practice session closer to your next career move.'

  const {
    selectedPack,
    isModalOpen,
    isProcessing,
    showSuccessModal,
    setShowSuccessModal,
    showPendingModal,
    setShowPendingModal,
    handleBuy,
    handleUnlock,
    handleCancel,
  } = useComboPricing()

  useEffect(() => {
    let cancelled = false
    void fetchProductPacks()
      .then((data) => {
        if (!cancelled) setCatalogPacks(data)
      })
      .catch(() => {
        // Price falls back to design default when catalog is unavailable.
      })
      .finally(() => {
        if (!cancelled) setCatalogReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    // In addition to the global `page_view` the analytics provider sends for every route:
    // this name is what keeps the four role pages separable on the marketing dashboards.
    // Both destinations get it — the first-party service for the dashboard, gtag for GA4.
    analytics.track(config.analytics.pageViewed, {
      campaign: config.analyticsSource,
      pageType: 'campaign',
    })
    gaEvent(config.analytics.pageViewed, {
      campaign: config.analyticsSource,
      page_path: config.path,
    })
  }, [config.analytics.pageViewed, config.analyticsSource, config.path])

  const currency = catalogReady ? getInterviewPracticeCurrency(catalogPacks) : 'INR'
  const displayPrice = catalogReady
    ? getInterviewPracticeDisplayPrice(catalogPacks, 'skill_assessment')
    : undefined

  /**
   * Every checkout entry goes through here — offer card, pricing dialog, and the
   * payment-required modal — so `checkout_started` cannot be fired by one and
   * forgotten by another.
   */
  const trackCheckoutStarted = useCallback(() => {
    analytics.track(config.analytics.checkoutStarted, {
      campaign: config.analyticsSource,
      pageType: 'campaign',
    })
    gaEvent(config.analytics.checkoutStarted, {
      campaign: config.analyticsSource,
      page_path: config.path,
    })
  }, [config.analytics.checkoutStarted, config.analyticsSource, config.path])

  /**
   * `checkout_started` fires where checkout actually begins, following the Dussehra
   * precedent (trackDussehraCheckout, which sits next to the /checkout navigation):
   *   · a signed-in visitor buying a combo pack navigates straight to /checkout from here;
   *   · an anonymous click only opens the login modal — handleBuy stores the pack and
   *     returns — so counting it would report checkouts that never started;
   *   · a simple pack checks out from the payment modal, which is unlockCheckout below.
   */
  const startCheckout = useCallback(
    (pack: ProductPack) => {
      if (isComboPack(pack) && getAuthToken()) trackCheckoutStarted()
      handleBuy(pack, config.path, config.analyticsSource)
    },
    [trackCheckoutStarted, config.path, config.analyticsSource, handleBuy],
  )

  const unlockCheckout = useCallback(() => {
    trackCheckoutStarted()
    return handleUnlock()
  }, [trackCheckoutStarted, handleUnlock])

  const startInterview = useCallback(
    async (ctaSurface: 'offer_card' | 'mobile_sticky') => {
      analytics.track(config.analytics.ctaClicked, {
        campaign: config.analyticsSource,
        ctaSurface: `${config.analyticsSource}_${ctaSurface}`,
        // The mock-interview funnel's "Signup started" step filters on this value.
        journey: 'resume',
      })
      gaEvent(config.analytics.ctaClicked, {
        campaign: config.analyticsSource,
        cta_surface: `${config.analyticsSource}_${ctaSurface}`,
        journey: 'resume',
      })
      setClaimError(null)
      setClaiming(true)
      try {
        let pack = findSkillAssessmentPack(catalogPacks)
        if (!pack) {
          pack = findSkillAssessmentPack(await fetchProductPacks())
        }
        if (!pack) {
          setClaimError(CAMPAIGN_CLAIM_UNAVAILABLE)
          return
        }
        startCheckout(pack)
      } catch {
        setClaimError(CAMPAIGN_CLAIM_UNAVAILABLE)
      } finally {
        setClaiming(false)
      }
    },
    [catalogPacks, config.analytics.ctaClicked, config.analyticsSource, startCheckout],
  )

  /**
   * Opens the pricing dialog and lazily loads the catalogue. Prices come from the same fetch the
   * offer card uses, so the dialog and the card can never disagree.
   */
  const openPricing = useCallback(() => {
    setPricingOpen(true)
    const pricingViewedEvent = campaignPricingViewedEvent(config.analyticsSource)
    if (pricingViewedEvent) {
      analytics.track(pricingViewedEvent, { source: config.analyticsSource })
      gaEvent(pricingViewedEvent, {
        campaign: config.analyticsSource,
        page_path: config.path,
      })
    }
    if (pricingPacks.length > 0) return
    setPricingLoading(true)
    void fetchProductPacks()
      .then((packs) => setPricingPacks(packs))
      .catch(() => {
        // Leave the list empty — the dialog renders its own unavailable state.
      })
      .finally(() => setPricingLoading(false))
  }, [config.analyticsSource, config.path, pricingPacks.length])

  // CSS background art cannot fire an error event, so probe the CDN URL once and swap to the
  // same-origin copy when it is missing. Only runs when a CDN base is configured at build time.
  const [pageBackgroundImage, setPageBackgroundImage] = useState(config.pageBackgroundImage)

  useEffect(() => {
    const src = config.pageBackgroundImage
    if (!src) return
    const local = localAssetPath(src)
    if (local === src) return
    const probe = new window.Image()
    probe.onerror = () => setPageBackgroundImage(local)
    probe.src = src
  }, [config.pageBackgroundImage])

  const hasPagePhoto = Boolean(config.pageBackgroundImage)

  return (
    <div
      data-campaign-path={config.path}
      className={`${styles.page} ${hasPagePhoto ? styles.pageWithPhotoBg : ''} ${inter.className} ${inter.variable} ${lato.variable}`}
    >
      {hasPagePhoto ? (
        <div
          className={styles.pageBackgroundPhoto}
          aria-hidden="true"
          style={{ backgroundImage: `url(${pageBackgroundImage})` }}
        />
      ) : (
        <>
          <div className={styles.pageBackground} aria-hidden="true" />
          <div className={styles.decorBelowHeader} aria-hidden="true">
            <div className={styles.heroDecorHalfCircle}>
              <div className={styles.heroDecorHalfCircleFill} />
            </div>
          </div>
        </>
      )}
      <div className={styles.pageOverscrollGuard} aria-hidden="true" />
      <div className={styles.headerChrome} aria-hidden="true" />
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link href="/" className={styles.logoLink}>
            <img
              src={CAMPAIGN_LOGO_ON_LIGHT}
              alt="meritlens"
              width={280}
              height={90}
              // Plain <img>, not next/image: the optimiser adds a /_next/image?url= hop that the
              // same-origin fallback cannot rewrite, and it is the hop that breaks on a CDN build.
              className={styles.logo}
              onError={handleAssetError}
            />
          </Link>
          {/* Opens the pricing overlay in place — the URL must not change mid-campaign. */}
          <button
            type="button"
            className={styles.pricingLink}
            onClick={openPricing}
          >
            Pricing
          </button>
        </div>
      </header>
      <div className={styles.pageHeaderSpacer} aria-hidden="true" />

      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          <CampaignHero key={config.path} config={config} />

          <OfferCard
            id={ids.offer}
            className={styles.offerCardInline}
            cardTitle={config.offer.cardTitle}
            bullets={config.offer.bullets}
            offerPreviewImage={offerPreviewImage}
            microcopy={offerMicrocopy}
            displayPrice={displayPrice}
            currency={currency}
            onStart={() => void startInterview('offer_card')}
            claiming={claiming}
            error={claimError}
          />

          <section id={ids.why} className={styles.whySection} aria-labelledby={ids.whyHeading}>
            <div className={styles.whyHeader}>
              <h2 id={ids.whyHeading} className={styles.whyHeading}>
                <span className={styles.whyHeadingGradient}>Why Practise with meritlens</span>
                <sup className={styles.tmMark}>TM</sup>?
              </h2>
              <p className={styles.whyIntro}>{config.why.intro}</p>
            </div>
            <ul ref={featureListRef} className={styles.featureList}>
              {config.why.features.map((feature, index) => (
                <li
                  key={feature.titleDesktop}
                  className={`${styles.featureCard} ${index % 2 === 1 ? styles.featureCardAlt : ''}`}
                >
                  <div className={styles.featureIconWrap}>
                    <img src={feature.icon} alt="" width={28} height={28} aria-hidden="true" onError={handleAssetError} />
                  </div>
                  <div className={styles.featureCopy}>
                    <h3 className={styles.featureTitleDesktop}>{feature.titleDesktop}</h3>
                    <h3 className={styles.featureTitleMobile}>{feature.titleMobile}</h3>
                    <p className={styles.featureDescription}>{feature.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <CampaignFooter footerId={ids.footer} />
        </div>

        <div className={styles.asideColumn}>
          <div className={styles.asideInner}>
            <OfferCard
              id={ids.offerDesktop}
              className={styles.offerCardSticky}
              cardTitle={config.offer.cardTitle}
              bullets={config.offer.bullets}
              offerPreviewImage={offerPreviewImage}
              microcopy={offerMicrocopy}
              displayPrice={displayPrice}
              currency={currency}
              onStart={() => void startInterview('offer_card')}
              claiming={claiming}
              error={claimError}
            />
          </div>
        </div>
      </div>

      <div className={styles.mobileStickyBar} aria-label="Purchase mock interview">
        <div className={styles.mobileStickyRow}>
          <div className={styles.mobileStickyPrice}>
            <span className={styles.mobilePriceAmount}>
              {displayPrice != null
                ? formatLandingPrice(displayPrice, currency)
                : formatLandingPrice(199, 'INR')}
            </span>
            {showLandingGstNote(currency) ? (
              <span className={styles.mobilePriceGst}>+GST</span>
            ) : null}
          </div>
          <button
            type="button"
            className={styles.mobileStickyCta}
            onClick={() => void startInterview('mobile_sticky')}
            disabled={claiming}
          >
            Start Interview
            <img src={CAMPAIGN_ICON_CTA_ARROW} alt="" width={16} height={16} aria-hidden="true" onError={handleAssetError} />
          </button>
        </div>
        <p className={styles.mobileStickyNote}>{offerMicrocopy}</p>
      </div>

      <PaymentRequiredModal
        isOpen={isModalOpen}
        isProcessing={isProcessing}
        // Displayed price, not the canonical one: the cards show display_price (199/899/1699) and the
        // modal has to agree with them, or the base price jumps by ₹1 on the last click.
        productPrice={selectedPack ? (selectedPack.display_price ?? selectedPack.price) : null}
        productName={selectedPack?.product_name ?? null}
        onUnlock={() => void unlockCheckout()}
        onCancel={handleCancel}
      />
      {showSuccessModal ? (
        <PaymentSuccessModal
          isOpen={showSuccessModal}
          attemptsAdded={1}
          onContinue={() => setShowSuccessModal(false)}
        />
      ) : null}
      <PaymentPendingModal isOpen={showPendingModal} onClose={() => setShowPendingModal(false)} />

      {pricingOpen && typeof document !== 'undefined'
        ? createPortal(
            <PricingDialog
              packs={pricingPacks}
              loading={pricingLoading}
              onClose={() => setPricingOpen(false)}
              onSelect={(pack) => {
                setPricingOpen(false)
                void startCheckout(pack)
              }}
            />,
            document.body,
          )
        : null}
    </div>
  )
}

export function InterviewPracticeCampaignPage({
  config,
}: {
  config: InterviewPracticeCampaignConfig
}) {
  return (
    <LandingLoginProvider>
      <CampaignBodyClass />
      <InterviewPracticeCampaignInner key={config.path} config={config} />
    </LandingLoginProvider>
  )
}
